window.RS7GTChapters = class {
  constructor(root) {
    this.root = root;
    this.nav = root.querySelector('[data-launch-chapters]');
    this.disclosure = this.nav?.querySelector('[data-chapters-disclosure]');
    this.list = this.nav?.querySelector('[data-chapters-list]');
    if (!this.nav || !this.disclosure || !this.list) return;
    this.controller = new AbortController();
    this.focusTargets = new Set();
    this.summary = this.disclosure.querySelector('summary');
    this.current = this.nav.querySelector('[data-chapters-current]');
    this.count = this.nav.querySelector('[data-chapters-count]');
    this.mobile = window.matchMedia('(max-width: 1099px)');
    this.items = [...root.querySelectorAll('#MainContent [data-launch-nav-label][id]')]
      .filter(element => element.dataset.launchNavLabel.trim())
      .map((element, index) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const number = document.createElement('span');
        const label = document.createElement('span');
        number.className = 'rs7-launch-chapters__number';
        number.setAttribute('aria-hidden', 'true');
        number.setAttribute('data-clock-reveal', '');
        label.setAttribute('data-clock-reveal', '');
        number.textContent = String(index + 1).padStart(2, '0');
        label.textContent = element.dataset.launchNavLabel.trim();
        link.href = `#${encodeURIComponent(element.id)}`;
        link.append(number, label);
        item.append(link);
        return { element, item, link, label: label.textContent };
      });
    this.list.replaceChildren(...this.items.map(item => item.item));
    if (!this.items.length) return;
    this.nav.hidden = false;
    this.root.setAttribute('data-has-chapters', '');
    const options = { signal: this.controller.signal };
    this.mobile.addEventListener('change', () => {
      this.setMode();
      this.root.refresh();
    }, options);
    this.summary.addEventListener('click', event => {
      if (!this.mobile.matches) event.preventDefault();
    }, options);
    this.disclosure.addEventListener('toggle', () => {
      if (!this.mobile.matches && !this.disclosure.open) this.disclosure.open = true;
      this.root.toggleAttribute('data-chapters-open', this.mobile.matches && this.disclosure.open);
      if (this.mobile.matches && this.disclosure.open) this.revealActiveLink();
    }, options);
    this.nav.addEventListener('click', event => {
      const item = this.items.find(item => item.link === event.target.closest('a[href]'));
      if (item && !event.button && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        this.close();
        if (!this.root.pager) {
          cancelAnimationFrame(this.focusFrame);
          this.focusFrame = requestAnimationFrame(() => this.focusTarget(item.element));
        }
      }
    }, options);
    this.nav.addEventListener('keydown', event => this.onKey(event), options);
    this.nav.addEventListener('focusin', event => { this.focusedControl = event.target; }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.nav.contains(event.target)) {
        this.close(this.list.contains(document.activeElement));
        this.focusedControl = null;
      }
    }, options);
    document.addEventListener('focusin', event => {
      if (!this.nav.contains(event.target)) {
        this.focusedControl = null;
        this.close();
      }
    }, options);
    this.setMode();
    this.refresh();
  }

  setMode() {
    // Hiding the summary at the breakpoint can blur it before matchMedia fires.
    const focused = document.activeElement === document.body ? this.focusedControl : document.activeElement;
    if (this.mobile.matches && this.list.contains(focused)) this.summary.focus({ preventScroll: true });
    this.disclosure.open = !this.mobile.matches;
    if (!this.mobile.matches && focused === this.summary) {
      this.items[this.activeIndex || 0].link.focus({ preventScroll: true });
    }
    this.root.removeAttribute('data-chapters-open');
  }

  revealActiveLink() {
    const link = this.items[this.activeIndex || 0].link;
    const bounds = link.getBoundingClientRect();
    const panel = this.list.getBoundingClientRect();
    if (bounds.top < panel.top) this.list.scrollTop -= panel.top - bounds.top;
    else if (bounds.bottom > panel.bottom) this.list.scrollTop += bounds.bottom - panel.bottom;
  }

  close(restoreFocus = false) {
    if (!this.mobile?.matches || !this.disclosure?.open) return;
    if (restoreFocus) this.summary.focus({ preventScroll: true });
    this.disclosure.open = false;
    this.root.removeAttribute('data-chapters-open');
  }

  focusTarget(target) {
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
      this.focusTargets.add(target);
      target.addEventListener('blur', () => {
        if (target.getAttribute('tabindex') === '-1') target.removeAttribute('tabindex');
        this.focusTargets.delete(target);
      }, { once: true, signal: this.controller.signal });
    }
    target.focus({ preventScroll: true });
  }

  containsOpenPanel(target) {
    return this.mobile?.matches && this.disclosure?.open && this.nav.contains(target);
  }

  onKey(event) {
    if (!this.containsOpenPanel(event.target)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey || (event.target === this.summary && event.key === ' ')) return;
    const direction = { ArrowDown: 1, ArrowUp: -1, PageDown: 1, PageUp: -1, ' ': event.shiftKey ? -1 : 1 }[event.key];
    if (!direction && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    if (event.key === 'Home' || event.key === 'End') this.list.scrollTop = event.key === 'Home' ? 0 : this.list.scrollHeight;
    else this.list.scrollTop += direction * (event.key.startsWith('Arrow') ? 40 : this.list.clientHeight * 0.8);
  }

  refresh() {
    if (!this.items?.length) return;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const headerHeight = this.root.header?.offsetHeight || 0;
    this.items.forEach(item => {
      const wrapper = item.element.closest('#MainContent > .shopify-section') || item.element;
      item.top = item.element === this.root.hero ? 0 : wrapper.getBoundingClientRect().top + window.scrollY;
      item.anchorTop = Math.max(0, Math.min(maxScroll, item.top - headerHeight));
    });
    this.update();
  }

  update() {
    if (!this.items?.length) return;
    const pager = this.root.pager;
    const stop = pager?.stops[pager.currentIndex()];
    let index = stop ? this.items.findIndex(item => item.element === stop.element) : -1;
    if (index < 0) {
      const position = stop?.y ?? window.scrollY;
      index = this.items.reduce((active, item, itemIndex) => (stop ? item.top : item.anchorTop) <= position + 2 ? itemIndex : active, 0);
    }
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.items.forEach((item, itemIndex) => {
      if (itemIndex === index) item.link.setAttribute('aria-current', 'location');
      else item.link.removeAttribute('aria-current');
    });
    const current = this.items[index].label;
    const count = `${String(index + 1).padStart(2, '0')} / ${String(this.items.length).padStart(2, '0')}`;
    if (this.root.clockReveal?.setText) {
      this.root.clockReveal.setText(this.current, current);
      this.root.clockReveal.setText(this.count, count);
    } else {
      this.current.textContent = current;
      this.count.textContent = count;
    }
  }

  destroy() {
    this.controller?.abort();
    cancelAnimationFrame(this.focusFrame);
    this.focusTargets?.forEach(target => {
      if (target.getAttribute('tabindex') === '-1') target.removeAttribute('tabindex');
    });
    this.focusTargets?.clear();
    this.root.removeAttribute('data-has-chapters');
    this.root.removeAttribute('data-chapters-open');
    if (this.nav) this.nav.hidden = true;
    if (this.disclosure) this.disclosure.open = false;
    this.list?.replaceChildren();
    this.focusedControl = null;
  }
};
